import Container from "react-bootstrap/Container";
import {Row} from "react-bootstrap";
import PropTypes from "prop-types";
import {Helmet} from "react-helmet";

const SECTIONS = [
    {
        level: 5,
        title: 'General Game Rules',
        intro: 'These rules apply to every Competition unless its competition-specific Game Rules say otherwise.',
        clauses: [
            {level: '5.1', text: 'You must register, pay and make your selections through the App.'},
            {
                level: '5.2',
                text: "Each Competition's entry fee, maximum number of Entries per person, start date and first Deadline are published in the App before entries open."
            },
            {
                level: '5.3',
                text: 'Each Entry is treated separately. If you hold more than one Entry, you may make the same or different selections for each.'
            },
            {
                level: '5.4',
                text: 'Deadlines. Selections and predictions must be made by the advertised Deadline. You may change them as often as you like until the Deadline. After the Deadline they are locked.'
            },
            {
                level: '5.5',
                text: 'Default selection. If you have not made a valid selection for a Last Player Standing Game Week by the Deadline, we will choose for you the highest-placed team in the relevant league table that you are still allowed to pick. This is your "default selection". Score prediction Competitions handle missed predictions as set out in section 7.'
            },
            {
                level: '5.6',
                text: 'Postponed fixtures. If a fixture you have selected is postponed before kick off and you do not change your selection before the Deadline, the result will be decided by the Football Pools Panel result, published at footballpools.com. In the unlikely event that a postponed fixture is not on the pools coupon, so there is no Pools Panel result, both teams in that fixture will go through.'
            },
            {
                level: '5.7',
                text: 'Abandoned fixtures. If a fixture has kicked off and is officially abandoned by the match referee or the competition organiser:',
                items: [
                    'if it is abandoned before half-time, the result is void and both teams go through; or',
                    'if it is abandoned at or after half-time, the score at the time of abandonment is treated as the final score. This applies even if the fixture is later replayed or the result is changed by the competition organiser. It applies to Last Player Standing games only. Abandoned fixtures in score prediction games are dealt with under clause 7.8.'
                ]
            },
            {
                level: '5.8',
                text: 'Rescheduled fixtures. Any fixture played out of sequence because of an earlier postponement or rescheduling is not included in a Game Week.'
            },
            {
                level: '5.9',
                text: 'Minimum fixtures. A Game Week needs at least 8 Premier League fixtures (Premskins) or 9 Championship fixtures (Champskins). If a Game Week has fewer than the minimum, it is void and no one is eliminated. The only exception is where none of the remaining players is affected by the missing fixtures, in which case the Game Week goes ahead as normal.'
            },
            {
                level: '5.10',
                text: 'Results. Results are based on the score at the end of normal time, including stoppage time, as officially recorded. Extra time and penalty shoot-outs do not count unless the Game Rules say otherwise. Later changes to a result (for example after an appeal) are ignored once a Game Week has been settled.'
            }
        ]
    },
    {
        level: 6,
        title: 'Last Player Standing games (Premskins and Champskins)',
        intro: 'Premskins uses Premier League fixtures and Champskins uses Championship fixtures. The rules are the same for both.',
        clauses: [
            {
                level: '6.1',
                text: 'Entry. Unless the Game Rules for a Competition say otherwise, each Entry costs £10 and you may make up to 3 Entries at the start of a Competition.'
            },
            {
                level: '6.2',
                text: 'Picking a team. Each Game Week you pick one team to win its fixture. If it wins, your Entry goes through to the next Game Week. If it draws or loses, your Entry is eliminated.'
            },
            {
                level: '6.3',
                text: 'No repeat picks. Once you have picked a team in a Competition, you cannot pick it again for the same Entry.'
            },
            {
                level: '6.4',
                text: 'Length of a Competition. A Competition ends when there is a winner under clause 6.7, or after 20 Game Weeks (Premskins) or 24 Game Weeks (Champskins), whichever comes first. If it reaches that limit, all remaining Entries share the Prize Pot equally. Most Competitions finish within 10 to 12 Game Weeks.'
            },
            {
                level: '6.5',
                text: 'Minimum elimination. A winner can only be declared once at least 90% of Entries have been eliminated. For example, with 5,000 Entries there must be 500 or fewer left. If more than 10% of Entries remain when the Competition ends under clause 6.4, the Prize Pot is shared equally among them.'
            },
            {
                level: '6.6',
                text: 'Final places payout. When the number of remaining Entries first falls to or below the threshold in the table, those Entries share 20% of the Prize Pot equally. The Competition then continues for the remaining 80%.',
                table: {
                    head: ['Total Prize Pot', 'Final places threshold'],
                    rows: [
                        ['Up to £50,000', '20 or fewer'],
                        ['£50,001 to £75,000', '30 or fewer'],
                        ['£75,001 to £100,000', '50 or fewer'],
                        ['Over £100,000', '100 or fewer']
                    ]
                }
            },
            {
                level: '6.7',
                text: 'Winning. The winner is the last Entry remaining. If all remaining Entries are eliminated in the same Game Week, they share the remaining Prize Pot equally.'
            },
            {
                level: '6.8',
                text: 'Agreed pot split. Remaining players may agree to end the Competition and share the remaining Prize Pot equally once the number of remaining Entries is at or below the limit in the table. Every remaining player must agree. If any player does not, the Competition continues. To request a split, every remaining player must email customerservices@communitygaming.co.uk before the next Deadline.',
                table: {
                    head: ['Total Prize Pot', 'Players who may agree a split'],
                    rows: [
                        ['Up to £20,000', 'Last 2'],
                        ['£20,001 to £75,000', 'Last 3 or fewer'],
                        ['£75,001 to £100,000', 'Last 5 or fewer'],
                        ['Over £100,000', 'Last 10 or fewer']
                    ]
                }
            },
            {
                level: '6.9',
                text: 'Survive variants. Some Competitions (for example Champskins-Survive) pay out part of the Prize Pot during the Competition. In these Competitions:',
                items: [
                    'after the Game Week stated in the Game Rules for that Competition, the Entries still in share 10% of the Prize Pot equally;',
                    'after each following Game Week, the Entries still in share 10% of the Prize Pot remaining at that point equally; and',
                    'the last Entry remaining wins whatever is left, under clause 6.7. The Game Week in which payouts start may vary between Survive Competitions and will be confirmed in the Game Rules for each one, together with whether clauses 6.6 and 6.8 also apply. We cannot offer cash-outs.'
                ]
            }
        ]
    },
    {
        level: 7,
        title: 'Score prediction games',
        intro: 'Score prediction Competitions, such as Champions League 26/27, are points-based rather than elimination-based.',
        clauses: [
            {
                level: '7.1',
                text: 'Entry. The entry fee and what it covers are set out in the Game Rules. A single entry fee may cover more than one Competition running over the same tournament, for example separate prizes for different stages. If so, the Game Rules will say how the entry fee is divided between the Prize Pots.'
            },
            {
                level: '7.2',
                text: 'Predictions. For each Round you predict the exact score of each fixture before the Deadline. Points are awarded for each prediction according to the points table in the Game Rules for that Competition. Points tables may vary from Competition to Competition.'
            },
            {
                level: '7.3',
                text: 'Missed predictions. If you do not make a prediction for a fixture by the Deadline, you score no points for it.'
            },
            {
                level: '7.4',
                text: 'Golden Team. Before the first Deadline you choose one Golden Team. Points you score on fixtures involving your Golden Team are doubled for the whole tournament. Your Golden Team cannot be changed after the first Deadline. If your Golden Team is eliminated from the tournament, no further doubling applies.'
            },
            {
                level: '7.5',
                text: "Jokers. You may play the number of Jokers set out in the Game Rules, when available. Each Joker doubles the points you score on one fixture of your choice and must be played before that fixture's Deadline. Only one Joker can be played on any one fixture, and a Joker cannot be played on a fixture involving your Golden Team. Unused Jokers have no value at the end of the Competition."
            },
            {
                level: '7.6',
                text: 'Winning. The Entry with the highest total points at the end of the Competition wins, or prizes are paid by finishing position if the Game Rules say so. If Entries are tied on points a tie break, relevant to the competition, and stated in the game rules, will be used.'
            },
            {
                level: '7.7',
                text: 'Mini Leagues. You can create or join private Mini Leagues to compare scores with friends. Mini Leagues are for fun only. They carry no separate prize from us and do not change how the Prize Pot is paid. You must not use a Mini League to arrange private stakes or side bets with other players.'
            },
            {
                level: '7.8',
                text: 'Postponed or abandoned fixtures. If a fixture is not completed before the start of the next game day of the tournament, it is void and no points are scored for it by anyone, unless the Game Rules say otherwise. Clauses 5.6 to 5.9 do not apply to score prediction games.'
            }
        ]
    }
];

const GameRules = (props) => {
    const {isMobile} = props;
    const render = () => {
        return (
            <div className={'w-100'}>
                <Helmet>
                    <title>Sportskins - Game Rules</title>
                    <meta name="description" content="Sportskins- Game Rules"/>
                </Helmet>
                <Container>
                    <Row>
                        <div className={'text-center fw-bold font-48 text-white cabin mt-5 mb-5'} role={'heading'}
                             aria-level={1}>
                            Game Rules
                        </div>
                    </Row>
                </Container>
                <section className={`bg-team-card-grey p-xxl-5 p-xl-5 p-lg-5 p-md-5 p-sm-1 mt-0 rounded-my-skins-top`}
                         aria-label={'SportSkins Game Rules'}>
                    <Container
                        className={'bg-white p-xxl-5 p-xl-5 p-lg-5 p-md-5 p-sm-1 pt-3 mt-0 rounded-my-skins-top'}>
                        <div className={`game-rules-ol ${isMobile ? 'm-1' : 'm-5'}`}>
                            {SECTIONS.map((section) => (
                                <div key={section.level}>
                                    <h2 className={'fs-4 fw-bold cabin text-black mt-4'}>
                                        {section.level}. {section.title}
                                    </h2>
                                    {section.intro && <p className={'cabin'}>{section.intro}</p>}
                                    <ol>
                                        {section.clauses.map((clause) => (
                                            <li key={clause.level} data-level={clause.level}>
                                                {clause.text}
                                                {clause.items && (
                                                    <ol>
                                                        {clause.items.map((item, i) => (
                                                            <li key={i} data-level={`(${i + 1})`}>{item}</li>
                                                        ))}
                                                    </ol>
                                                )}
                                                {clause.table && (
                                                    <table className={'table table-bordered mt-2 w-auto'}>
                                                        <thead>
                                                        <tr>
                                                            {clause.table.head.map((h) => <th key={h}>{h}</th>)}
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {clause.table.rows.map((row) => (
                                                            <tr key={row[0]}>
                                                                {row.map((cell) => <td key={cell}>{cell}</td>)}
                                                            </tr>
                                                        ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            ))}
                        </div>
                    </Container>

                </section>

            </div>
        );
    };

    return render();
}


GameRules.propTypes = {
    isMobile: PropTypes.bool.isRequired
};
export default GameRules;
